var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useEffect, useState, memo, useCallback, } from 'react';
import { Box, Typography, Grid, TextField, IconButton, Dialog, DialogTitle, DialogContent, Button, MenuItem, } from '@mui/material';
import { Clear, Add, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
// Internal dependencies
import { appStyles } from '../App.styles';
import { getAuthHeader, UserContext } from '../auth';
import { axiosClient } from '../axios';
import { LOG_LEVELS, SERVICES, ENDPOINTS } from '../constants';
import { SAMPLE_PROMPTS, PROMPT_CATEGORIES } from './constants';
import { PromptForm } from './PromptForm';
import { PromptsGrid } from './PromptsGrid';
/**
 * Prompt component
 * @param props Props
 * @returns Prompt element
 */
export const Prompts = memo(function ({ handlePromptClick, handlePromptsClose, open }) {
    // Get user credential
    const user = useContext(UserContext);
    // Get translation function
    const { t, i18n } = useTranslation();
    // Empty prompt form
    const emptyPromptForm = {
        promptId: '',
        promptName: '',
        promptContent: '',
    };
    // Current Prompts list
    const [prompts, setPrompts] = useState(SAMPLE_PROMPTS);
    // Custom prompts list
    const [customPrompts, setCustomPrompts] = useState([]);
    // Fetch and update document when user credential changed
    useEffect(() => {
        const getPrompts = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                // Get autheHeader
                const authHeader = yield getAuthHeader(user);
                // Get headers
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get document
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.GET_PROMPTS}`, { headers });
                // Extract document
                const { prompts } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                // Set custom prompt
                setCustomPrompts([...prompts]);
                // Set prompts
                setPrompts([...prompts, ...SAMPLE_PROMPTS]);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Chat, useEffect, getPrompts, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.AI_CHAT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getPrompts();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, useEffect, getPrompts, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [user]);
    // Custom form open status
    const [customFormOpen, setCustomFormOpen] = useState(false);
    /**
     * Handle custom form open click
     */
    const handleCustomFormOpen = useCallback((promptForm) => {
        setPromptForm(promptForm);
        setCustomFormOpen(true);
    }, []);
    /**
     * Handle custom form close click
     */
    const handleCustomFormClose = () => {
        setPromptForm(emptyPromptForm);
        setCustomFormOpen(false);
    };
    // Prompt category
    const [promptCategory, setPromptCategory] = useState(PROMPT_CATEGORIES[0]);
    const handlePromptCategory = (event) => {
        try {
            // Update prompt category
            setPromptCategory(event.target.value);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, handlePromptCategory, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    };
    // Prompt form
    const [promptForm, setPromptForm] = useState(emptyPromptForm);
    const [/** Search text */ searchText, setSearchText] = useState('');
    const [
    /** Search text in lower case */ searchTextInLowerCase, setSearchTextInLowerCase,] = useState('');
    /**
     * Handle search change
     * @param e Event
     */
    const handleSearchChange = (e) => {
        var _a;
        try {
            const searchTextInput = (((_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value) || '');
            // Update prompt category
            setSearchText(searchTextInput);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, handleSearchChange, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    };
    /**
     * Handle search key up
     * @param event Keyboard event
     */
    const handleSearchKeyUp = (event) => __awaiter(this, void 0, void 0, function* () {
        try {
            if (event.key === 'Enter') {
                setSearchTextInLowerCase(searchText.toLowerCase());
            }
            ;
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Prompts, handleSearchKeyUp, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    });
    useEffect(() => {
        try {
            // Filter prompts whenever promptCatergory or searchText change
            // List down all prompts
            const allPrompts = [...customPrompts, ...SAMPLE_PROMPTS];
            // Update filtered prompts list
            const filteredPrompts = allPrompts.filter((prompt) => ((promptCategory === PROMPT_CATEGORIES[0] || (prompt === null || prompt === void 0 ? void 0 : prompt.category) === promptCategory)
                && (t(prompt.promptName).toLowerCase().includes(searchTextInLowerCase))));
            setPrompts([...filteredPrompts]);
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Prompts, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [promptCategory, searchTextInLowerCase]);
    return (_jsxs(Dialog, Object.assign({ sx: {
            '& .MuiDialog-paper': {
                backgroundColor: '#ffffff',
                borderRadius: '1rem',
                maxWidth: '1024px',
                width: '100%',
                height: {
                    sm: '80%',
                    xs: '90%',
                },
            }
        }, open: open, onClose: handlePromptsClose }, { children: [_jsxs(DialogTitle, Object.assign({ sx: {
                    padding: {
                        sm: '1.5rem 1.5rem 0',
                        xs: '0.5rem 0.5rem 0',
                    }
                } }, { children: [_jsxs(Grid, Object.assign({ container: true, sx: { padding: '0 0 1.5rem', flexWrap: 'nowrap' } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                } }, { children: _jsx(Typography, Object.assign({ sx: {
                                        padding: '0',
                                        fontWeight: '400',
                                        fontSize: '1.75rem',
                                        lineHeight: '2.25rem',
                                        color: '#000000',
                                        margin: '0 0.5rem 0 0',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        width: 'calc(100%)',
                                        whiteSpace: 'nowrap',
                                    } }, { children: t('Prompts') })) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Button, Object.assign({ "aria-label": "Open custom form button", variant: "outlined", sx: {
                                        border: '1px dashed #707974',
                                        borderRadius: '100px',
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                    }, startIcon: _jsx(Add, {}), onClick: () => { handleCustomFormOpen(emptyPromptForm); } }, { children: t('Custom') })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(IconButton, Object.assign({ "aria-label": "Close prompt button", onClick: handlePromptsClose }, { children: _jsx(Clear, { sx: { color: '#1C1B1F' } }) })) }))] })), _jsxs(Grid, Object.assign({ container: true, sx: { width: '100%', justifyContent: 'space-between' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true, sx: { margin: '0 0.5rem 0 0' } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: {
                                        padding: '7px 12px',
                                        border: '1px solid',
                                        borderColor: 'greyLight.main',
                                        borderRadius: '0.25rem',
                                        boxSizing: 'border-box',
                                        maxWidth: '234px',
                                        width: '100%',
                                    } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: {
                                                margin: '0 0.5rem 0 0',
                                                display: 'flex',
                                                alignItems: 'center',
                                            } }, { children: _jsx(Search, { sx: {
                                                    width: '19px',
                                                    height: '20px',
                                                    color: 'blackDark.main',
                                                } }) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                '& input': {
                                                    padding: '0',
                                                }
                                            } }, { children: _jsx(TextField
                                            // Company text field
                                            , { 
                                                // Company text field
                                                variant: 'standard', name: 'promptName', placeholder: `${t('Blog Post')}`, value: searchText, onChange: handleSearchChange, onKeyUp: handleSearchKeyUp, autoComplete: "off", sx: { width: '100%' }, InputProps: {
                                                    disableUnderline: true,
                                                    style: {
                                                        color: 'blackDark.main',
                                                        padding: '0',
                                                        boxSizing: 'border-box',
                                                    },
                                                }, inputProps: {
                                                    style: Object.assign(Object.assign({}, appStyles.text12), { padding: '0', overflow: 'auto' }),
                                                } }) }))] })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(TextField, Object.assign({ sx: {
                                        color: '#082018',
                                        backgroundColor: '#CEE9DD',
                                        borderRadius: '8px',
                                        border: 'none',
                                        "& fieldset": { border: 'none' },
                                        maxWidth: {
                                            xs: '96px',
                                            ls: 'none',
                                        },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: 'calc(100%)',
                                    }, select: true, value: promptCategory, InputProps: { sx: { borderRadius: '8px', border: 'none' }, }, inputProps: { sx: Object.assign(Object.assign({}, appStyles.text14), { padding: '0.375rem 0.5rem' }) }, onChange: handlePromptCategory }, { children: PROMPT_CATEGORIES.map((promptCategory) => (_jsx(MenuItem, Object.assign({ value: promptCategory }, { children: t(promptCategory) }), promptCategory))) })) }))] }))] })), _jsx(DialogContent, Object.assign({ sx: {
                    padding: {
                        sm: '1.5rem',
                        xs: '0.5rem',
                    },
                } }, { children: _jsxs(Box, Object.assign({ sx: { overflow: 'auto', height: `calc(100%)`, } }, { children: [_jsx(Box, { component: PromptForm, promptForm: promptForm, setPrompts: setPrompts, setCustomPrompts: setCustomPrompts, promptCategory: promptCategory, setCustomFormOpen: setCustomFormOpen, customFormOpen: customFormOpen }), _jsx(Grid, Object.assign({ container: true, spacing: {
                                sm: 3,
                                xs: 2,
                            }, sx: {
                                padding: '1.5rem 0 0'
                            } }, { children: _jsx(PromptsGrid, { prompts: prompts, handlePromptClick: handlePromptClick, handlePromptsClose: handlePromptsClose, handleCustomFormOpen: handleCustomFormOpen }) }))] })) }))] })));
});
